/**
 * Workspace domain management for mobile/desktop apps.
 *
 * On mobile (Capacitor) and desktop (Tauri), the app needs to know which
 * workspace domain to connect to. On web, the domain comes from the URL bar.
 */

const WORKSPACE_DOMAIN_KEY = 'panluma_workspace_domain';
const DEFAULT_DOMAIN = 'app.panluma.ai';

export function getStoredWorkspaceDomain(): string | null {
  return localStorage.getItem(WORKSPACE_DOMAIN_KEY);
}

export function setStoredWorkspaceDomain(domain: string): void {
  localStorage.setItem(WORKSPACE_DOMAIN_KEY, domain);
}

export function clearStoredWorkspaceDomain(): void {
  localStorage.removeItem(WORKSPACE_DOMAIN_KEY);
}

export function getWorkspaceBaseUrl(): string {
  const domain = getStoredWorkspaceDomain();
  return domain ? `https://${domain}` : `https://${DEFAULT_DOMAIN}`;
}

export interface WorkspaceTenantInfo {
  tenant_name: string;
  domain_type: string;
  logo_url?: string | null;
}

/**
 * Confirm a host is a real, active PanLuma workspace by calling the public,
 * rate-limited `GET /api/v1/auth/tenant/resolve` on that host (the middleware
 * resolves the tenant from the request Host, so only a registered, routable
 * domain returns a tenant). Returns the tenant info or null.
 *
 * Used by `WorkspaceSelector` (the user types/picks the workspace they want to
 * bind the app to — they explicitly choose to talk to that backend). Times out
 * so a slow/hostile host can't hang the caller; the response shape is checked
 * structurally (a bare 200 with no `tenant_name` is rejected) rather than cast
 * blindly. NOTE: this probes the *candidate* host, so it proves reachability,
 * not DB registration — it is NOT used as the web deep-link open-redirect guard
 * (that only redirects inside the panluma.ai zone; see workspaceDomainRedirect).
 * No `Content-Type` header so it stays a simple (non-preflighted) CORS GET.
 */
export async function validateWorkspaceDomain(
  domain: string,
): Promise<WorkspaceTenantInfo | null> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 8000);
  try {
    const response = await fetch(`https://${domain}/api/v1/auth/tenant/resolve`, {
      method: 'GET',
      signal: controller.signal,
    });
    if (!response.ok) {
      return null;
    }
    const data = await response.json();
    const info = data?.data;
    return info && typeof info.tenant_name === 'string'
      ? (info as WorkspaceTenantInfo)
      : null;
  } catch {
    return null;
  } finally {
    clearTimeout(timeout);
  }
}

export { DEFAULT_DOMAIN };
